import React, { createContext, useContext } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * "HOLD EVERYTHING STILL."
 *
 * The export page vectorises live components. The capture is a synchronous
 * walk of computed styles, so whatever is unfinished at that instant is what
 * lands in the file — and an export frame is usually OFF-SCREEN, which means
 * `whileInView` animations have never fired at all. A card whose entrance
 * starts at `opacity: 0` exports blank; a strike-through that draws from
 * `scaleX: 0` exports undrawn.
 *
 * Threading a `settled` prop down to every nested detail is impractical when
 * the artefact is five components deep (the mosaic cards are), so this is the
 * same contract expressed as context: the export page wraps its frames in
 * `StillProvider`, and any component that animates asks `useStill()` instead
 * of `useReducedMotion()`.
 *
 * `useStill()` deliberately folds in `prefers-reduced-motion`, because the two
 * cases want exactly the same thing: render the END state, immediately, with
 * no motion. Components that already take an explicit `settled` prop (SlackPanel,
 * ChatGPTPanel, BrainFlowExperiment) keep it — this is for everything nested
 * too deep to reach.
 */
const StillContext = createContext(false);
export function StillProvider({
  children
}: {
  children: React.ReactNode;
}) {
  return <StillContext.Provider value={true} data-id="element-1073" data-mp="3gtxpnob33bkd">{children}</StillContext.Provider>;
}

/** True when motion must be skipped and the finished state rendered directly. */
export function useStill(): boolean {
  const reduce = useReducedMotion();
  const still = useContext(StillContext);
  return !!reduce || still;
}